"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Clock, Star, Cpu, ShieldCheck, Zap, Smartphone, Users, Settings, ArrowRight, Tag } from "lucide-react";
const trustFeatures = [{
  icon: Clock,
  title: "Rapid Deployment",
  description: "Agile solutions in record time"
}, {
  icon: Star,
  title: "Expert Engineers",
  description: "Certified industry specialists"
}, {
  icon: Cpu,
  title: "Scalable Tech",
  description: "Built for future growth"
}, {
  icon: Tag,
  title: "Strategic Value",
  description: "ROI-driven IT investments"
}];
const services = [{
  name: "Custom Software",
  icon: Cpu,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_custom_software.png"
}, {
  name: "Cybersecurity",
  icon: ShieldCheck,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_cybersecurity.png"
}, {
  name: "AI & Automation",
  icon: Zap,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_ai_automation.png"
}, {
  name: "Web & Mobile App Development",
  icon: Smartphone,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_web_mobile.png"
}, {
  name: "IT Consulting",
  icon: Users,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_consulting.png"
}, {
  name: "Managed IT Services",
  icon: Settings,
  link: "/TheApexStoryPage",
  image_url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/service_managed_it.png"
}];
function CategoriesGrid() {
  return <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
      {services.map((service, i) => <Link key={i} to={service.link} data-index={i} className="group flex flex-col items-center gap-4">
          <div className="relative flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg bg-accent shadow-md transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl">
            {service.image_url ? <img src={service.image_url} data-wvc-srcset={`${service.image_url}-wvc-srcset`} data-wvc-sizes={`${service.image_url}-wvc-sizes`} alt={service.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" /> : <service.icon className="h-10 w-10 text-primary/40" />}
            <div className="absolute inset-0 bg-primary/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          </div>
          <span className="font-default text-center text-base font-semibold text-foreground transition-colors group-hover:text-primary">
            {service.name}
          </span>
        </Link>)}
    </div>;
}
export default function FeaturedSolutionsHub() {
  return <section data-section-id="259623" id="featured-solutions-hub" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div className="flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <h1 className="font-default text-5xl font-bold tracking-tight text-foreground sm:text-6xl xl:text-7xl">
                Smart Tech. <br />
                <span className="text-primary">Apex Results.</span>
              </h1>
              <p className="max-w-[600px] text-lg text-muted-foreground md:text-xl leading-relaxed font-default">
                Empowering Australian businesses with cutting-edge IT solutions, from custom software to advanced cybersecurity and AI automation.
              </p>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button size="lg" className="h-14 px-8 text-base rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity" asChild>
                <Link to="/ConnectPage">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base rounded-md border-2 border-primary text-primary hover:bg-primary/5" asChild>
                <Link to="/InsightsPage">Our Insights</Link>
              </Button>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[500px] lg:max-w-none">
            <div className="absolute -inset-4 z-0 rotate-3 rounded-lg bg-primary/20 blur-2xl transition-transform duration-700 hover:rotate-6" />
            <div className="absolute -inset-2 z-0 -rotate-2 rounded-lg bg-secondary/10 blur-xl" />
            <div className="relative z-10 aspect-square overflow-hidden rounded-lg border-4 border-border shadow-2xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/it_solutions_dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/it_solutions_dashboard.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/it_solutions_dashboard.png-wvc-sizes" alt="Advanced IT Solutions Dashboard" className="h-full w-full object-cover" loading="eager" />
            </div>
            
            <div className="absolute -bottom-6 -left-6 z-20 flex items-center gap-3 rounded-lg bg-card p-4 shadow-xl border border-border/50">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Star className="h-6 w-6 fill-current" />
              </div>
              <div>
                <p className="font-default text-sm font-bold text-foreground">Enterprise Grade</p>
                <p className="text-xs text-muted-foreground">Trusted by 500+ firms</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24 rounded-lg bg-card p-8 shadow-lg border border-border/40">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {trustFeatures.map((feature, index) => <div key={index} data-index={index} className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <feature.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-default text-base font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-default">{feature.description}</p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="mt-24">
          <div className="flex items-center justify-between">
            <h2 className="font-default text-3xl font-bold text-foreground md:text-4xl">
              Core IT Solutions
            </h2>
            <Button variant="ghost" className="hidden sm:flex text-primary hover:bg-primary/5" asChild>
              <Link to="/TheApexStoryPage">
                Explore All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          <CategoriesGrid />
          
          <div className="mt-8 flex justify-center sm:hidden">
            <Button variant="outline" className="w-full rounded-md border-primary text-primary" asChild>
              <Link to="/TheApexStoryPage">View All Services</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}